import { create } from 'zustand'

import { useAuthStore } from './authStore'

/**
 * Break-glass super admin access (FERPA plan, owner decisions 5 and 10): the super admin must say why
 * before reading student records (system admins are school officials and are never asked,
 * decision 9). The reason travels in `X-Access-Reason` on every request until it is ended, and is stored on
 * each `PLATFORM_SUPPORT` access event, where the school can see it.
 */

export const ACCESS_REASON_HEADER = 'X-Access-Reason'
export const ACCESS_REASON_REQUIRED_CODE = 'access_reason_required'
export const ACCESS_REASON_MAX_LENGTH = 500
const STORAGE_KEY = 'maktab.support-access-reason'

/** Collapse whitespace (headers cannot carry line breaks), trim and cap at the API limit. */
export function normalizeAccessReason(input: string): string {
  return input.replace(/\s+/g, ' ').trim().slice(0, ACCESS_REASON_MAX_LENGTH)
}

/**
 * Header-safe form of the reason. Browsers refuse header values outside Latin-1, so a reason
 * with other characters (Arabic, curly quotes, emoji) is sent RFC 8187-style:
 * `UTF-8''<percent-encoded>`. Plain ASCII is sent as typed.
 */
export function encodeAccessReasonHeader(reason: string): string {
  return /^[\x20-\x7e]*$/.test(reason) ? reason : `UTF-8''${encodeURIComponent(reason)}`
}

function readStored(): string | null {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY)
    return value ? normalizeAccessReason(value) || null : null
  } catch {
    return null
  }
}

function writeStored(reason: string | null) {
  try {
    if (reason) sessionStorage.setItem(STORAGE_KEY, reason)
    else sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // private mode / storage disabled: the in-memory reason still works for this tab
  }
}

export type AccessReasonPromptMode = 'required' | 'change'

interface AccessReasonState {
  reason: string | null
  promptOpen: boolean
  promptMode: AccessReasonPromptMode
  setReason: (reason: string) => void
  clearReason: () => void
  /** Open the dialog to replace the current reason (no request is waiting). */
  openChangePrompt: () => void
  /** Dialog submitted: store the reason and release every waiting request. */
  submitPrompt: (reason: string) => void
  /** Dialog dismissed: waiting requests fail with their original error. */
  cancelPrompt: () => void
}

type Waiter = { resolve: (reason: string) => void; reject: (error: Error) => void }
let waiters: Waiter[] = []

export class AccessReasonCancelledError extends Error {
  constructor() {
    super('Super admin access reason was not provided')
    this.name = 'AccessReasonCancelledError'
  }
}

export const useAccessReasonStore = create<AccessReasonState>(set => ({
  reason: readStored(),
  promptOpen: false,
  promptMode: 'required',

  setReason: input => {
    const reason = normalizeAccessReason(input) || null
    writeStored(reason)
    set({ reason })
  },

  clearReason: () => {
    writeStored(null)
    set({ reason: null })
  },

  openChangePrompt: () => set({ promptOpen: true, promptMode: 'change' }),

  submitPrompt: input => {
    const reason = normalizeAccessReason(input)
    if (!reason) return
    writeStored(reason)
    set({ reason, promptOpen: false })
    const pending = waiters
    waiters = []
    for (const waiter of pending) waiter.resolve(reason)
  },

  cancelPrompt: () => {
    set({ promptOpen: false })
    const pending = waiters
    waiters = []
    for (const waiter of pending) waiter.reject(new AccessReasonCancelledError())
  },
}))

/**
 * Ask the person why they are opening student records. Concurrent callers share one open
 * dialog; all of them resolve with the same reason (or reject together on cancel).
 */
export function requestAccessReason(): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    waiters.push({ resolve, reject })
    const state = useAccessReasonStore.getState()
    if (!state.promptOpen || state.promptMode !== 'required') {
      useAccessReasonStore.setState({ promptOpen: true, promptMode: 'required' })
    }
  })
}

/** Test helper: forget waiting requests and reset state. */
export function resetAccessReasonStoreForTests() {
  waiters = []
  writeStored(null)
  useAccessReasonStore.setState({ reason: null, promptOpen: false, promptMode: 'required' })
}

// A reason belongs to one signed-in session: sign-out (or a user switch) ends it.
// (`?.`: unit tests may replace the auth store with a partial mock.)
useAuthStore.subscribe?.((state, previous) => {
  const signedOut = !state.accessToken && Boolean(previous.accessToken)
  const switchedUser =
    Boolean(previous.user?.id) && Boolean(state.user?.id) && previous.user?.id !== state.user?.id
  if (signedOut || switchedUser) {
    useAccessReasonStore.getState().cancelPrompt()
    useAccessReasonStore.getState().clearReason()
  }
})
